import { type AnnualCashFlow } from '@fruitful/financial-calculators';
import { formatDollars } from '@fruitful/fruitils';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useState } from 'react';
import {
    BodyMediumRegular,
    H5Emphasized,
    Slider,
    XStack,
    YStack,
} from 'smoothie';

import { PageContentContainer } from '../../common/components/GroveContainers';
import {
    getCalendarYear,
    getYearIndex,
} from '../utils/retirementCalculatorUtils';
import { AnnualCashFlowAssetGrowth } from './AnnualCashFlowAssetGrowth';
import { AnnualCashFlowDataTable } from './AnnualCashFlowDataTable';
import { AnnualCashFlowSankey } from './AnnualCashFlowSankey';

interface AnnualCashFlowsProps {
    annualCashFlows: AnnualCashFlow[];
    selectedAmount: number;
}

export const AnnualCashFlows = ({
    annualCashFlows,
    selectedAmount,
}: AnnualCashFlowsProps) => {
    const [selectedYear, setSelectedYear] = useState(1); // Year 1 is the first year and is the next calendar year.
    const selectedYearIndex = getYearIndex(selectedYear);

    if (
        annualCashFlows.length === 0 ||
        annualCashFlows[selectedYearIndex] == null
    ) {
        return null;
    }

    return (
        <PageContentContainer>
            <YStack width="100%">
                <H5Emphasized>
                    Annual Cash Flows for Contribution Amount:{' '}
                    {formatDollars(selectedAmount)}
                </H5Emphasized>

                {/* Year Slider */}
                <XStack
                    gap={24}
                    alignItems="center"
                    width="100%"
                    marginTop={16}
                >
                    <XStack gap={16} alignItems="center">
                        <BodyMediumRegular>Year:</BodyMediumRegular>
                        <Slider
                            min={1}
                            max={Math.max(
                                1,
                                (
                                    annualCashFlows[
                                        annualCashFlows.length - 1
                                    ] as AnnualCashFlow
                                ).year,
                            )}
                            step={1}
                            disabled={annualCashFlows.length <= 1}
                            value={[selectedYear]}
                            onSlideMove={(_, value: number) => {
                                const validValue = Math.max(
                                    1,
                                    Math.min(
                                        Math.floor(value),
                                        (
                                            annualCashFlows[
                                                annualCashFlows.length - 1
                                            ] as AnnualCashFlow
                                        ).year,
                                    ),
                                );
                                setSelectedYear(validValue);
                            }}
                        />
                        <BodyMediumRegular>
                            Year {selectedYear} ({getCalendarYear(selectedYear)}
                            )
                        </BodyMediumRegular>
                    </XStack>

                    {/* Member Ages */}
                    {annualCashFlows[selectedYearIndex] != null && (
                        <XStack gap={16} alignItems="center">
                            {annualCashFlows[selectedYearIndex]
                                .primaryMemberAge != null && (
                                <YStack
                                    gap={4}
                                    padding={12}
                                    backgroundColor="$gray100"
                                    borderRadius={8}
                                    borderWidth={1}
                                    borderColor="$gray200"
                                >
                                    <BodyMediumRegular color="$gray1000">
                                        Member 1 Age:{' '}
                                        <BodyMediumRegular>
                                            {
                                                annualCashFlows[
                                                    selectedYearIndex
                                                ].primaryMemberAge
                                            }
                                        </BodyMediumRegular>
                                    </BodyMediumRegular>
                                </YStack>
                            )}
                            {annualCashFlows[selectedYearIndex]
                                ?.partnerMemberAge != null && (
                                <YStack
                                    gap={4}
                                    padding={12}
                                    backgroundColor="$gray100"
                                    borderRadius={8}
                                    borderWidth={1}
                                    borderColor="$gray200"
                                >
                                    <BodyMediumRegular color="$gray1000">
                                        Member 2 Age:{' '}
                                        <BodyMediumRegular>
                                            {
                                                annualCashFlows[
                                                    selectedYearIndex
                                                ].partnerMemberAge
                                            }
                                        </BodyMediumRegular>
                                    </BodyMediumRegular>
                                </YStack>
                            )}
                        </XStack>
                    )}
                </XStack>

                {/* Tab View for various charts, tables, views for Annual Cash Flow data */}
                <TabView
                    style={{ width: '100%' }}
                    panelContainerStyle={{
                        width: '100%',
                        padding: 0,
                        marginTop: 32,
                    }}
                >
                    <TabPanel header="Asset Growth">
                        <AnnualCashFlowAssetGrowth
                            annualCashFlows={annualCashFlows}
                            selectedYear={selectedYear}
                            onYearSelect={setSelectedYear}
                        />
                    </TabPanel>
                    <TabPanel header="Cash Flow">
                        <AnnualCashFlowSankey
                            annualCashFlows={annualCashFlows}
                            selectedYear={selectedYear}
                        />
                    </TabPanel>
                    <TabPanel header="Data Table">
                        <AnnualCashFlowDataTable
                            annualCashFlows={annualCashFlows}
                        />
                    </TabPanel>
                </TabView>
            </YStack>
        </PageContentContainer>
    );
};
